
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";
import { useTheme } from "@/hooks/useTheme";

import { Converter } from "@/containers/Converter";
import { Head } from "@/components/Head";

import { MagicianSection } from "@/sections/MagicianSection";
import { ShareDesignSection } from "@/sections/ShareDesignSection";
import { ConversionSection } from "@/sections/ConversionSection";
import { TestimonialsSection } from "@/sections/TestimonialsSection";
import { SupportFeaturesSection } from "@/sections/SupportFeaturesSection";
import { HowItWorksStepsSection } from "@/sections/HowItWorksStepsSection";
import { CTASection } from "@/sections/CTASection";
import { TrustedBySection } from "@/sections/TrustedBySection";

const FigmaToBubble = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <>
      <Head
        pageNamespace="page/FigmaToBubble"
        ogImage="/images/figma2bubble-og-image.jpg"
      />

      <MagicianSection
        title={t("page/FigmaToBubble:title")}
        subtitle={t("page/FigmaToBubble:subtitle")}
        magicianLogoFile="bubble"
        cta={{
          title: t("section/magicians:seeHowItWorks"),
          scrollToId: "HowItWorksStepsSection",
        }}
      >
        <Converter disabled />
      </MagicianSection>

      <TrustedBySection />

      <ConversionSection
        title={t("page/FigmaToBubble:section/conversion.title")}
        iconBadges={[
          {
            iconType: "SHEETS",
            text: t("page/FigmaToBubble:section/conversion.badges.supportsAll"),
          },
          {
            iconType: "SYNC-ICON",
            text: t(
              "page/FigmaToBubble:section/conversion.badges.convertsComponentsSymbols",
              {
                format: "XD",
              },
            ),
          },
          {
            iconType: "CHECK-CIRCLED-EMPTY",
            text: t("page/FigmaToBubble:section/conversion.badges.accuracy"),
          },
        ]}
        elements={{
          from: {
            iconType: "FIGMA",
            image: "/images/figma-bubble-preview.jpg",
            title: "Figma Demo Preview",
          },
          to: {
            iconType: "BUBBLE",
            image: "/images/bubble-preview.jpg",
            title: "Bubble Demo Preview",
          },
        }}
      />

      <ShareDesignSection
        id="ShareDesignSection"
        title={
          ((
            <Trans
              i18nKey="page/FigmaToBubble:section/shareDesign.title"
              components={[<span key="0" style={{ color: colors.ORANGE }} />]}
            />
          ) as unknown) as string
        }
        image={{
          src: "/images/figma-to-bubble-converted-file-elements.jpg",
          alt: t("page/FigmaToBubble:section/shareDesign.imageAlt"),
        }}
        backgroundColor={colors.LIGHT_GRAY1}
        cta={{
          title: t("page/FigmaToBubble:section/shareDesign.ctaButton"),
        }}
        elements={[
          {
            id: "ELEMENT#1",
            iconType: "SPREAD-ICON",
            title: t(
              "page/FigmaToBubble:section/shareDesign.elements.pathElementsTitle",
            ),
            text: t(
              "page/FigmaToBubble:section/shareDesign.elements.pathElementsText",
            ),
          },
          {
            id: "ELEMENT#2",
            iconType: "FOLDER-SIMPLE-ICON",
            title: t(
              "page/FigmaToBubble:section/shareDesign.elements.folderStructureTitle",
            ),
            text: t(
              "page/FigmaToBubble:section/shareDesign.elements.folderStructureText",
            ),
          },
          {
            id: "ELEMENT#3",
            iconType: "CHECK-CIRCLED-EMPTY",
            title: t(
              "page/FigmaToBubble:section/shareDesign.elements.imageSupportTitle",
            ),
            text: t(
              "page/FigmaToBubble:section/shareDesign.elements.imageSupportText",
            ),
          },
        ]}
      />

      <SupportFeaturesSection
        title={t("page/FigmaToBubble:section/supportFeatures.title")}
        subtitle={t("page/FigmaToBubble:section/supportFeatures.subtitle")}
        cta={{
          title: t("common:actions.doNow", { do: t("common:actions.signUp") }),
        }}
        elements={[
          {
            src: "/images/figma-bubble-feature-layers.png",
            title: t(
              "page/FigmaToBubble:section/supportFeatures.features.layers.title",
            ),
            text: t(
              "page/FigmaToBubble:section/supportFeatures.features.layers.text",
            ),
          },
          {
            src: "/images/figma-bubble-feature-pages.png",
            title: t(
              "page/FigmaToBubble:section/supportFeatures.features.pages.title",
            ),
            text: t(
              "page/FigmaToBubble:section/supportFeatures.features.pages.text",
            ),
          },
          {
            src: "/images/figma-bubble-feature-symbols.png",
            title: t(
              "page/FigmaToBubble:section/supportFeatures.features.symbols.title",
            ),
            text: t(
              "page/FigmaToBubble:section/supportFeatures.features.symbols.text",
            ),
          },
        ]}
      />
      <HowItWorksStepsSection
        title={t("page/FigmaToBubble:section/howItWorksSteps.title")}
        subtitle={t("page/FigmaToBubble:section/howItWorksSteps.subtitle")}
        sourceSteps={{
          conversionType: "figma",
          icon: "FIGMA",
          steps: [
            {
              id: 0,
              title: t(
                "page/FigmaToBubble:section/howItWorksSteps.sourceSteps.step1.title",
              ),
              text: t(
                "page/FigmaToBubble:section/howItWorksSteps.sourceSteps.step1.text",
              ),
            },
            {
              id: 1,
              title: t(
                "page/FigmaToBubble:section/howItWorksSteps.sourceSteps.step2.title",
              ),
              text: t(
                "page/FigmaToBubble:section/howItWorksSteps.sourceSteps.step2.text",
              ),
            },
            {
              id: 2,
              title: t(
                "page/FigmaToBubble:section/howItWorksSteps.sourceSteps.step3.title",
              ),
              text: t(
                "page/FigmaToBubble:section/howItWorksSteps.sourceSteps.step3.text",
              ),
            },
          ],
        }}
        targetSteps={{
          conversionType: "bubble",
          icon: "BUBBLE",
          steps: [
            {
              id: 0,
              title: t(
                "page/FigmaToBubble:section/howItWorksSteps.targetSteps.step1.title",
              ),
              text: t(
                "page/FigmaToBubble:section/howItWorksSteps.targetSteps.step1.text",
              ),
            },
            {
              id: 1,
              title: t(
                "page/FigmaToBubble:section/howItWorksSteps.targetSteps.step2.title",
              ),
              text: t(
                "page/FigmaToBubble:section/howItWorksSteps.targetSteps.step2.text",
              ),
            },
          ],
        }}
      />
      <TestimonialsSection />
      <CTASection
        title={t("section/cta:titles.figmaToBubblePage")}
        href="/converter/xd-to-sketch"
        isBetaFormat
      />
    </>
  );
};

// Temporal workaround for https://github.com/vercel/next.js/discussions/18396
 async function _getStaticProps() {
  return {
    props: {},
  };
}

export default FigmaToBubble;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/converter/figma-to-bubble',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  